import { syncCarouselControls } from './carousel.js';

/**
 * Initialize Splide carousels on dynamic carousel elements.
 *
 * Initializes Splide on any element with .carousel-dynamic class.
 * Splide library must be loaded in the document.
 *
 * @since 1.0.0
 */
function getCarouselList(carousel) {
	/*
	 * Dynamic carousel groups are often assembled in the block editor by placing
	 * a Query Loop, a taxonomy loop, or another list-producing block inside the
	 * `.carousel-dynamic` wrapper. Splide still needs one list element with the
	 * `splide__list` class, so we look for the project conventions first and
	 * only fall back to the first unordered list when no known class is present.
	 *
	 * Scope the search to `.splide__track` and exclude `.splide__pagination` so
	 * Splide's pagination `<ul>` is never mistaken for the slide list after mount.
	 */
	const listSelector =
		'.splide__list, .wp-block-post-template, .cards-categories-list, ul:not(.splide__pagination)';
	const track = carousel.querySelector('.splide__track');

	if (track) {
		return track.querySelector(listSelector);
	}

	return carousel.querySelector(listSelector);
}

function prepareCarouselMarkup(carousel) {
	const list = getCarouselList(carousel);

	if (!list) {
		return false;
	}

	carousel.classList.add('splide');
	list.classList.add('splide__list');

	/*
	 * Only direct children of the carousel list should become slides. The old
	 * implementation targeted every `li` inside the carousel, which could turn
	 * nested list content inside a card into Splide slides by accident.
	 */
	Array.from(list.children).forEach((item) => {
		item.classList.add('splide__slide');
	});

	if (!list.parentElement.classList.contains('splide__track')) {
		const track = document.createElement('div');

		track.className = 'splide__track';
		list.parentNode.insertBefore(track, list);
		track.appendChild(list);
	}

	return true;
}

function getDefaultPerPage(carousel) {
	const columnsClass = Array.from(carousel.classList).find((className) =>
		/^carousel-dynamic-\d+$/.test(className)
	);

	if (!columnsClass) {
		return 3;
	}

	const columns = Number.parseInt(columnsClass.replace('carousel-dynamic-', ''), 10);

	return Number.isNaN(columns) || columns < 1 ? 3 : columns;
}

const taxonomyResizeDebounceMs = 180;
const taxonomyResizeCarousels = new Set();
let taxonomyResizeBound = false;
let taxonomyResizeTimeout = null;

function isTaxonomyLoopCarousel(carousel) {
	return Boolean(
		carousel.querySelector('.taxonomy-query-loop--carousel, .pacecore-taxonomy-query-loop__item')
	);
}

function refreshTaxonomyCarouselOnResize(carousel) {
	if (carousel.hasAttribute('hidden') || carousel.classList.contains('is-carousel-panel-hidden')) {
		return;
	}

	const splide = carousel.splide;

	if (!splide) {
		return;
	}

	const currentIndex = splide.index ?? 0;

	splide.refresh();

	/*
	 * Keep the current page after refresh when possible so manual resizes don't
	 * unexpectedly jump to another slide.
	 */
	if (splide.length > 0) {
		const boundedIndex = Math.max(0, Math.min(currentIndex, splide.length - 1));
		splide.go(boundedIndex);
	}

	syncCarouselControls(carousel);
}

function bindTaxonomyCarouselResizeSync(carousel) {
	taxonomyResizeCarousels.add(carousel);

	if (taxonomyResizeBound) {
		return;
	}

	taxonomyResizeBound = true;

	window.addEventListener(
		'resize',
		() => {
			if (taxonomyResizeTimeout) {
				window.clearTimeout(taxonomyResizeTimeout);
			}

			taxonomyResizeTimeout = window.setTimeout(() => {
				window.requestAnimationFrame(() => {
					taxonomyResizeCarousels.forEach((taxonomyCarousel) => {
						refreshTaxonomyCarouselOnResize(taxonomyCarousel);
					});
				});
			}, taxonomyResizeDebounceMs);
		},
		{ passive: true }
	);
}

/**
 * Skip inactive tab panels inside carousel-sidebar-anchor (lazy-init on tab switch).
 *
 * @param {Element} carousel
 * @returns {boolean}
 */
export function shouldSkipSidebarCarouselPanel(carousel) {
	if (!carousel.closest('.carousel-sidebar-anchor')) {
		return false;
	}

	if (!carousel.hasAttribute('data-carousel-panel')) {
		return false;
	}

	return (
		carousel.hasAttribute('hidden') || carousel.classList.contains('is-carousel-panel-hidden')
	);
}

/**
 * Initialize Splide on a single `.carousel-dynamic` element.
 *
 * @param {Element} carousel
 * @returns {object|null} Splide instance when mounted
 */
export function initSingleDynamicCarousel(carousel) {
	/*
	 * Editors can place a carousel-capable block inside an existing carousel
	 * pattern. When that happens, the inner carousel owns the track/list and the
	 * outer wrapper should stay as layout only, otherwise Splide will try to
	 * mount twice against the same slides.
	 */
	if (carousel.querySelector('.carousel-dynamic')) {
		return null;
	}

	if (carousel.dataset.dynamicCarouselInitialized === 'true') {
		return carousel.splide ?? null;
	}

	if (shouldSkipSidebarCarouselPanel(carousel)) {
		return null;
	}

	if (typeof Splide === 'undefined') {
		console.warn('Splide library is not loaded');
		return null;
	}

	if (!prepareCarouselMarkup(carousel)) {
		return null;
	}

	carousel.dataset.dynamicCarouselInitialized = 'true';

	const perPage = getDefaultPerPage(carousel);
	const isSidebarAnchorCarousel = Boolean(carousel.closest('.carousel-sidebar-anchor'));
	const isAutoplayCarousel = carousel.classList.contains('carousel-autoplay');

	const isCarouselDynamic2 = carousel.classList.contains('carousel-dynamic-2');

	const mobilePerPage = isSidebarAnchorCarousel
		? 1
		: isCarouselDynamic2
			? 2
			: Math.max(1, Math.ceil(perPage / 2));

	const splideOptions = {
		/*
		 * Splide only recognizes 'slide', 'loop', and 'fade' as types. An
		 * invalid value such as 'carousel' silently disables the slide-specific
		 * logic in Splide's Controller: when `perMove` equals `perPage` and the
		 * final page is partial, getNext()/getPrev() only snap to the last
		 * reachable page when `isSlide` is true. Without it, Splide reports no
		 * next slide and leaves the next arrow permanently disabled even though
		 * the pagination dots can still reach that page. Use 'slide' so the
		 * arrows enable/disable correctly.
		 */
		type: 'slide',
		perPage,
		perMove: perPage,
		gap: '1.5rem',
		breakpoints: {
			992: {
				perPage: mobilePerPage,
				perMove: mobilePerPage,
				gap: '1rem',
			},
		},
	};

	if (isAutoplayCarousel) {
		splideOptions.autoplay = true;
		splideOptions.interval = 5000;
		splideOptions.pauseOnHover = false;
		splideOptions.pauseOnFocus = true;
	}

	const splide = new Splide(carousel, splideOptions);

	const Autoplay =
		window.splide?.Extensions?.Autoplay ?? window.Splide?.Extensions?.Autoplay;
	const extensions = isAutoplayCarousel && Autoplay ? { Autoplay } : {};

	if (Object.keys(extensions).length) {
		splide.mount(extensions);
	} else {
		splide.mount();
	}
	carousel.splide = splide;

	if (isTaxonomyLoopCarousel(carousel)) {
		bindTaxonomyCarouselResizeSync(carousel);
	}

	return splide;
}

export function initDynamicCarousel() {
	const carousels = document.querySelectorAll('.carousel-dynamic');

	if (carousels.length === 0) {
		return;
	}

	carousels.forEach((carousel) => {
		initSingleDynamicCarousel(carousel);
	});
}
